import {
  CHANNEL_SYNC_ADVICE,
  MAX_PARCELS,
  PACKING_SHORTAGE_LABELS,
  PACKING_SHORTAGE_REASONS,
  cancelledLinesText,
  channelSyncWarningText,
  scanSpellings,
  type PackingLine,
  type PackingShortageReason,
  type PackingShortageResult,
  type PackingVerification,
  type UrgentAction,
  type VerifyAndPackResponse,
} from '@wms/domain';
import { useEffect, useState } from 'react';
import { ApiError, api, apiBlob, errorMessage, type FloorAction } from '../api';
import { feedback } from '../feedback';
import { fmt, statusLabel } from '../format';
import { printPdf, printZpl } from '../printing';
import { navigate } from '../router';
import { store, useSession } from '../session';
import type { CartonType, OrderSummary, PackingStation } from '../types';
import { ChannelBadge, Empty, ErrorBanner, Modal, ProductThumb, ScanField, SellerChip, Spinner, StatusChip, useFloorAction, useLoad, useToast, type ActionWords, type FloorActionRunner } from '../ui';
import { DocumentPreview, desiText } from '../ui/documents';
import { Icon } from '../ui/icons';
import { NextWorkStep } from '../ui/NextStep';
import type { SuggestSource } from '../suggest';
import { pileOf } from '../dock';
import { labelCheck, neededText } from '../parcels';
import { labelCode, ProductLabelDialog } from './catalog/ProductLabel';
import { LabelState, LabelStateCell, phone, PrintQueue, rememberDesi, usePrintStates } from './DeskPrinter';
import { toteText } from './PickRoutes';
import { relabelBanner, relabelChip, relabelFor } from '../urgent';
import { useUrgent } from './Urgent';

const STATION_KEY = 'wms.station';
/** what a manager typing an order number at the desk is offered: the orders waiting to be packed or being packed */
const PACKING_ORDERS: SuggestSource = { kinds: ['order'], orderStatuses: ['picked', 'packing'] };

/** The packing table: pick the desk once, then scan order after order. */
export function Packing({ orderId, onChange }: { orderId: string | null; onChange: () => void }) {
  const { warehouseId } = useSession();
  const query = warehouseId ? `?warehouseId=${warehouseId}` : '';
  const stations = useLoad(() => api<PackingStation[]>('GET', `/api/wms/stations${query}`), [query]);
  const cartons = useLoad(() => api<CartonType[]>('GET', `/api/wms/cartons${query}`), [query]);
  const [stationCode, setStationCode] = useState(() => store.get(STATION_KEY));
  // the desi written onto an order's label when this desk prints it (our labels print none by default)
  const [labelDesi, setLabelDesi] = useState<ReadonlyMap<string, string>>(new Map());

  if (stations.error) return <ErrorBanner message={stations.error} onRetry={() => void stations.reload()} />;
  if (!stations.data) return <Spinner />;
  const active = stations.data.filter((s) => s.isActive);
  const station = active.find((s) => s.code === stationCode) ?? null;

  if (!station) {
    return (
      <section className="card">
        <div className="card-h">
          <h2>Paketleme masanızı seçin</h2>
          <span className="sub">Bu cihaz hatırlar; etiketler masanın yazıcısına gider</span>
        </div>
        <div className="card-b grid">
          {active.length === 0 && <Empty title="Tanımlı paketleme masası yok">Depo yöneticisi /api/wms/stations ile masa tanımlamalı.</Empty>}
          {active.map((s) => (
            <button
              key={s.id}
              className="card kpi"
              style={{ cursor: 'pointer', textAlign: 'left' }}
              onClick={() => {
                store.set(STATION_KEY, s.code);
                setStationCode(s.code);
              }}
            >
              <span className="n mono">{s.code}</span>
              <span className="l">{s.name}</span>
            </button>
          ))}
        </div>
      </section>
    );
  }

  return (
    <div className="split">
      <div className="stack">
        {orderId ? (
          <PackOrder
            key={orderId}
            orderId={orderId}
            station={station}
            cartons={cartons.data ?? []}
            onChange={onChange}
            warehouseQuery={query}
            desi={labelDesi.get(orderId) ?? null}
            onDesi={(desi) => {
              // the desk's printer computer writes it onto the label (this computer, when it is that one)
              rememberDesi(orderId, desi);
              setLabelDesi((all) => new Map(all).set(orderId, desi ?? ''));
            }}
          />
        ) : (
          <PackQueue warehouseQuery={query} />
        )}
      </div>
      <div className="stack">
        <section className="card">
          <div className="card-h">
            <Icon name="box" />
            <h2>
              Masa <span className="mono">{station.code}</span>
            </h2>
            <span className="sub">{station.name}</span>
            <span className="spacer" />
            <button
              className="btn sm"
              onClick={() => {
                store.set(STATION_KEY, null);
                setStationCode(null);
              }}
            >
              Değiştir
            </button>
          </div>
        </section>
        <PrintQueue station={station} />
      </div>
    </div>
  );
}

/** Opens the order behind a scanned order number or tracking number, if it is at the tables. */
function useOrderOpener(warehouseQuery: string) {
  const toast = useToast();
  const amp = warehouseQuery ? `&${warehouseQuery.slice(1)}` : '';
  return async (code: string) => {
    let found: OrderSummary[];
    try {
      found = await api<OrderSummary[]>('GET', `/api/orders?search=${encodeURIComponent(code)}&limit=5${amp}`);
    } catch (e) {
      toast(errorMessage(e), 'bad');
      return;
    }
    // the code as read; failing that, as a reader on another keyboard layout meant it (the search finds both)
    const named = (spelling: string) => found.find((o) => o.orderNumber.toUpperCase() === spelling || o.trackingNumber === spelling);
    const exact = scanSpellings(code).map(named).find((o) => o !== undefined) ?? (found.length === 1 ? found[0] : undefined);
    if (!exact) {
      feedback('error');
      toast(found.length === 0 ? `${code}: sipariş bulunamadı` : 'Birden fazla sipariş eşleşti, listeden seçin', 'bad');
      return;
    }
    // a packed parcel opens too: its label printed again, or the packing taken back
    if (exact.status !== 'picked' && exact.status !== 'packing' && exact.status !== 'packed') {
      feedback('error');
      toast(`${exact.orderNumber}: ${statusLabel(exact.status)}`, 'bad');
      return;
    }
    navigate(`/paketleme/${exact.id}`);
  };
}

/** A packed order's shipping label printed again through this computer's printer. */
async function printShippingLabel(orderId: string): Promise<void> {
  const blob = await apiBlob(`/api/orders/${orderId}/documents/shipping_label`);
  if (blob.type.startsWith('text/')) await printZpl(await blob.text(), 'Kargo etiketi');
  else await printPdf(blob);
}

/** Orders waiting at the tables; a scan of the order number or the tracking number opens one. */
function PackQueue({ warehouseQuery }: { warehouseQuery: string }) {
  const amp = warehouseQuery ? `&${warehouseQuery.slice(1)}` : '';
  const queue = useLoad(() => api<OrderSummary[]>('GET', `/api/orders?status=picked,packing&sort=oldest&limit=200${amp}`), [amp], 10_000);
  // parcels packed but not handed over: their label can be printed again from here
  const packed = useLoad(() => api<OrderSummary[]>('GET', `/api/orders?status=packed&sort=newest&limit=30${amp}`), [amp], 30_000);
  const labels = usePrintStates(packed.data?.map((o) => o.id) ?? []);
  const onPhone = phone();
  const open = useOrderOpener(warehouseQuery);
  const toast = useToast();
  // orders whose carrier (or tracking number) changed after their label printed (acil iş): the old label comes off
  const relabel = new Map((useUrgent()?.actions ?? []).filter((a) => a.kind === 'relabel').map((a) => [a.orderId as string, a]));

  return (
    <>
      <section className="card">
        <div className="card-b">
          <ScanField
            placeholder="Sipariş no veya kargo takip no okutun"
            allowTyping
            onScan={open}
            suggest={PACKING_ORDERS}
            hint="Toplama sepetindeki sipariş etiketini ya da kargo etiketini okutun; siparişi listeden de açabilirsiniz."
          />
        </div>
      </section>
      <section className="card">
        <div className="card-h">
          <h2>Paketlenmeyi bekleyenler</h2>
          <span className="sub">{queue.data?.length ?? 0} sipariş · en eski üstte</span>
        </div>
        {queue.error && <ErrorBanner message={queue.error} onRetry={() => void queue.reload()} />}
        {!queue.data ? (
          <Spinner />
        ) : queue.data.length === 0 ? (
          <Empty title="Masada bekleyen sipariş yok">Toplanan siparişler burada belirir.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <tbody>
                {queue.data.map((o) => (
                  <tr key={o.id} className="clickable" onClick={() => navigate(`/paketleme/${o.id}`)}>
                    <td>
                      <span className="mono">{o.orderNumber}</span> <ChannelBadge channel={o.channel} />
                    </td>
                    <td>
                      <SellerChip code={o.tenantCode} />
                    </td>
                    <td>
                      {o.pickTote && <span className="chip nowrap">{toteText(o.pickTote)}</span>}
                      {o.repackSince && (
                        <span className="chip bad nowrap" title="Paketlendikten sonra bir ürünü iptal edildi: paketi Sevkiyat'tan alın, açıp yeniden paketleyin">
                          Yeniden paketle
                        </span>
                      )}
                      {relabel.has(o.id) && (
                        <span className="chip bad nowrap" title="Etiketi basıldıktan sonra kargo firması ya da takip numarası değişti: eski etiketi sökün, yeni etiket bu masadan basılır">
                          {relabelChip(relabel.get(o.id)!.details)}
                        </span>
                      )}
                      {o.channelSync && (
                        <span className="chip warn nowrap" title={`${channelSyncWarningText(o.channelSync)}. ${CHANNEL_SYNC_ADVICE}`}>
                          Pazaryeri okunmuyor
                        </span>
                      )}
                    </td>
                    <td className="num">{o.units} adet</td>
                    <td>
                      <StatusChip status={o.status} />
                    </td>
                    <td className="muted">{fmt.age(o.orderedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {(packed.data?.length ?? 0) > 0 && (
        <section className="card">
          <div className="card-h">
            <h2>Paketlenenler</h2>
            <span className="sub">Kargoya verilmedi · etiketi yeniden basın ya da paketlemeyi geri almak için açın</span>
          </div>
          <div className="table-wrap">
            <table className="t stack-sm">
              <tbody>
                {packed.data!.map((o) => (
                  <tr key={o.id} className="clickable" onClick={() => navigate(`/paketleme/${o.id}`)}>
                    <td>
                      <span className="mono">{o.orderNumber}</span> <ChannelBadge channel={o.channel} />
                    </td>
                    <td>
                      <SellerChip code={o.tenantCode} />
                    </td>
                    <td className="mono muted">
                      {o.trackingNumber ?? '—'}
                      {relabel.has(o.id) && (
                        <span className="chip bad nowrap" style={{ marginLeft: 6 }} title="Kargo değişti: yeni etiketi paketteki eski etiketin üstüne yapıştırıp masada, Sevkiyat’ta ya da Acil işler ekranında okutun">
                          Yeni etiket
                        </span>
                      )}
                    </td>
                    <td className="num">
                      <span className="print-state">
                        {/* a phone has no label printer: the label goes to the desk's printer from there */}
                        <LabelStateCell states={labels.get(o.id)} again={onPhone} />
                        {!onPhone && (
                          <button
                            className="btn sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              printShippingLabel(o.id).catch((err: unknown) => toast(errorMessage(err), 'bad'));
                            }}
                          >
                            <Icon name="printer" size={16} /> Etiketi bas
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

/** waiting: every unit is scanned but the label or a seller's document is not ready yet */
type Outcome = { tone: 'ok' | 'bad' | 'warn'; text: string; waiting?: 'label' | 'document' } | null;

function PackOrder(props: {
  orderId: string;
  station: PackingStation;
  cartons: readonly CartonType[];
  onChange: () => void;
  warehouseQuery: string;
  /** the desi to write onto this order's label when it prints; null: none */
  desi: string | null;
  onDesi: (desi: string | null) => void;
}) {
  const { orderId, station, cartons, onChange } = props;
  const { isSupervisor } = useSession();
  const openNext = useOrderOpener(props.warehouseQuery);
  const loaded = useLoad(() => api<PackingVerification>('GET', `/api/wms/packing/${orderId}`), [orderId]);
  const [verification, setVerification] = useState<PackingVerification | null>(null);
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [carton, setCarton] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [hit, setHit] = useState<string | null>(null);
  const [flash, setFlash] = useState('');
  // "Desi ekle": before packing, for the label the desk prints; after, to print it again with the desi
  const [desiOpen, setDesiOpen] = useState(false);
  const [reprintWithDesi, setReprintWithDesi] = useState(false);
  const [productLabel, setProductLabel] = useState<PackingLine | null>(null);
  // "Eksik / hasarlı": the line whose units cannot go into the parcel
  const [shortLine, setShortLine] = useState<PackingLine | null>(null);
  // the labels after packing were looked at: one box after all, or the boxes counted
  const [labelsChecked, setLabelsChecked] = useState(false);
  // after packing: the koli sayısı control opened from the desk's buttons
  const [parcelsOpen, setParcelsOpen] = useState(false);
  // the desk's buttons and "Eksik / hasarlı": one whose answer was lost is sent again with its key, so
  // it counts once; given up on, it may have counted: the desk waits for the server's state
  const [unsure, setUnsure] = useState(false);
  const desk = useFloorAction({
    onUnsure: () => {
      setUnsure(true);
      void loaded.reload();
    },
  });
  useEffect(() => {
    if (!unsure) return;
    setVerification(null);
    setUnsure(false);
  }, [loaded.data]);
  const deskWaits = desk.waiting || unsure;
  // a product scan whose answer was lost: "geri al" would take back the scan before it
  const [scanHeld, setScanHeld] = useState(false);
  // KARGO DEĞİŞTİ read: the desk goes on once the packer says the old label is off
  const [relabelSeen, setRelabelSeen] = useState(false);
  const urgent = useUrgent();
  const view = verification ?? loaded.data ?? null;

  const answer = (response: VerifyAndPackResponse) => {
    if (response.verification) setVerification(response.verification);
    if (response.ok) {
      const completed = response.verification.status === 'completed';
      // packed with fewer labels than it may need: another sound, and the question right below
      const check = completed ? labelCheck(response.verification) : null;
      feedback(check ? 'warn' : completed ? 'done' : 'ok');
      setHit(response.verification.lastScan?.orderItemId ?? null);
      setOutcome(
        check
          ? { tone: check.kind === 'short' ? 'bad' : 'warn', text: `Paketlendi. ${check.units} adet, ${response.verification.parcels} kargo etiketi: kolileri kontrol edin.` }
          : completed
            ? {
                tone: 'ok',
                // goods labelled at the bin print nothing more
                text: response.verification.completion?.printJobs.length === 0 ? 'Paketlendi.' : 'Paketlendi. Etiket masanın yazıcısına gönderildi.',
              }
            : { tone: 'ok', text: `+${response.verification.lastScan?.units ?? 1} adet` },
      );
      if (completed) {
        onChange();
        // packed here, a relabel or a repack of it is done: the red bar goes at once
        if (view?.relabel || view?.repack) void urgent?.reload();
      }
    } else {
      feedback('error');
      const waiting = response.error.code === 'LABEL_NOT_READY' ? 'label' : response.error.code === 'WAYBILL_NOT_READY' ? 'document' : undefined;
      setOutcome({ tone: waiting ? 'warn' : 'bad', text: response.error.message, ...(waiting ? { waiting } : {}) });
    }
    setFlash('');
    requestAnimationFrame(() => setFlash(response.ok ? 'flash-ok' : 'flash-bad'));
    setQuantity(1);
  };

  const scan = async (barcode: string, byEye = false, count = quantity, action?: FloorAction) => {
    try {
      answer(
        await api<VerifyAndPackResponse>(
          'POST',
          '/api/wms/verify-and-pack',
          {
            stationId: station.code,
            orderId,
            barcode,
            ...(count > 1 ? { quantity: count } : {}),
            ...(carton ? { cartonCode: carton } : {}),
            ...(byEye ? { byEye: true } : {}),
          },
          { refusalIsAnswer: true, action },
        ),
      );
    } catch (e) {
      feedback('error');
      setOutcome({ tone: 'bad', text: errorMessage(e) });
    }
  };

  // geri al: the latest scan comes off the counter (all the units it counted)
  const undoScan = async (action?: FloorAction) => {
    try {
      const next = await api<PackingVerification>('POST', `/api/wms/packing/${orderId}/undo-last-scan`, undefined, { action });
      setVerification(next);
      feedback('ok');
      setOutcome({ tone: 'warn', text: 'Son okutma geri alındı. Ürünü koliden çıkarıp doğru ürünü okutun.' });
    } catch (e) {
      feedback('error');
      setOutcome({ tone: 'bad', text: errorMessage(e) });
    }
  };

  // a jammed or smudged label, or one that never printed: the stored one again through this computer's printer
  const reprint = async () => {
    try {
      await printShippingLabel(orderId);
    } catch (e) {
      setOutcome({ tone: 'bad', text: errorMessage(e) });
    }
  };

  // koli sayısı: one label page per box; a packed order's labels print again at the desk
  const saveParcels = async (count: number, action?: FloorAction) => {
    try {
      const next = await api<PackingVerification>('POST', `/api/wms/packing/${orderId}/parcels`, { stationId: station.code, count }, { action });
      setVerification(next);
      setParcelsOpen(false);
      feedback('ok');
      if (next.status === 'completed') {
        setLabelsChecked(true);
        setOutcome({ tone: 'ok', text: `${count} kargo etiketi masanın yazıcısına gönderildi. Her koliye bir etiket yapıştırın.` });
      }
    } catch (e) {
      feedback('error');
      setOutcome({ tone: 'bad', text: errorMessage(e) });
    }
  };

  // geri al after packing: back to the desk with every unit counted, the stock back on the books
  const unpack = async (action?: FloorAction) => {
    try {
      const back = await api<PackingVerification>('POST', `/api/wms/packing/${orderId}/unpack`, undefined, { action });
      setVerification(back);
      // packed again later, the labels are looked at again
      setLabelsChecked(false);
      setParcelsOpen(false);
      feedback('ok');
      const ownBox = back.lines.every((l) => l.shipsAsIs) && back.parcels >= back.lines.reduce((n, l) => n + l.required, 0);
      setOutcome({
        tone: 'warn',
        text: ownBox
          ? 'Paketleme geri alındı. Hazır olunca “Etiketi bas, tamamla” ile yeniden tamamlayın.'
          : 'Paketleme geri alındı. Koliyi düzeltin; “Paketlemeyi tamamla” ile yeniden paketleyin ya da son okutmayı geri alın.',
      });
      onChange();
    } catch (e) {
      feedback('error');
      setOutcome({ tone: 'bad', text: errorMessage(e) });
    }
  };

  const retry = async (action?: FloorAction) => {
    try {
      answer(await api<VerifyAndPackResponse>('POST', `/api/wms/packing/${orderId}/complete`, { stationId: station.code, cartonCode: carton }, { refusalIsAnswer: true, action }));
    } catch (e) {
      setOutcome({ tone: 'bad', text: errorMessage(e) });
    }
  };

  // eksik / hasarlı: the order goes on hold, the parcel waits on the BEKLEYEN shelf
  // (on the desk's runner: a report sent again after its dialog closed still lands here)
  const reportShortage = async (line: PackingLine, reason: PackingShortageReason, qty: number, action?: FloorAction) => {
    let result: PackingShortageResult;
    try {
      result = await api<PackingShortageResult>(
        'POST',
        `/api/wms/packing/${orderId}/shortage`,
        { stationId: station.code, orderItemId: line.orderItemId, qty, reason, expectedMissing: line.missing },
        { action },
      );
    } catch (e) {
      setOutcome({ tone: 'bad', text: errorMessage(e) });
      // reported already (sent twice): the desk shows the order as the server has it
      if (e instanceof ApiError && e.code === 'SHORTAGE_CHANGED') {
        setShortLine(null);
        setUnsure(true);
        void loaded.reload();
      }
      throw e;
    }
    setVerification(result.verification);
    setShortLine(null);
    feedback('ok');
    setOutcome({
      tone: 'warn',
      text:
        reason === 'damaged'
          ? `Hasarlı ürünü hasarlı alana (${result.damagedArea ?? '—'}) bırakın.`
          : reason === 'wrong_product'
            ? 'Yanlış gelen ürünü koliye koymayın, vardiya sorumlusuna verin.'
            : `Bildirildi. ${result.countBins.join(', ')} ${result.countBins.length > 1 ? 'gözleri' : 'gözü'} sayılacak.`,
    });
    onChange();
  };

  // goods that leave in their own box: the picker read every unit, the desk only prints the label
  const packAsIs = async (action?: FloorAction) => {
    try {
      answer(await api<VerifyAndPackResponse>('POST', `/api/wms/packing/${orderId}/as-is`, { stationId: station.code }, { refusalIsAnswer: true, action }));
    } catch (e) {
      setOutcome({ tone: 'bad', text: errorMessage(e) });
    }
  };

  useEffect(() => {
    if (!hit) return;
    const timer = setTimeout(() => setHit(null), 700);
    return () => clearTimeout(timer);
  }, [hit]);

  if (loaded.error && !view) return <ErrorBanner message={loaded.error} onRetry={() => void loaded.reload()} />;
  if (!view) return <Spinner />;
  const completed = view.status === 'completed';
  const required = view.lines.reduce((n, l) => n + l.required, 0);
  const packed = view.lines.reduce((n, l) => n + l.packed, 0);
  // 2+ units on one label, or fewer labels than own-box units: asked after packing
  const check = labelCheck(view);
  // before packing: the boxes the goods need at least (each own-box unit is one, the rest share one)
  const ahead = labelCheck({ lines: view.lines, parcels: 1 });
  const changeParcels = (count: number) => void desk.run('Koli sayısını kaydet', (action) => saveParcels(count, action));
  // every unit scanned in an earlier visit, the order still open (its label or a document was not ready)
  const allScanned = !completed && required > 0 && packed >= required;
  // units that each leave in their own box, a label page each: the picker read them, the desk only prints the label
  const asIs = !completed && required > 0 && view.lines.every((l) => l.shipsAsIs) && view.parcels >= required;
  // a unit was reported missing or broken: the order waits in the exceptions queue, nothing is scanned
  const held = !completed && view.holdReason !== null;
  // the ways to finish stay on screen after a refused scan; a wait has its own button
  const finishOffered = !outcome?.waiting && !held;
  // the carrier changed after the label printed (acil iş, 0069): nothing is scanned or packed before the
  // packer read it and took the old label off
  const blocked = !completed && view.relabel !== undefined && !relabelSeen;
  const deskBusy = deskWaits || blocked;
  // a parcel packed before its carrier changed, open here: its new label read here closes the acil iş (as on
  // #/acil and at Sevkiyat), an old one is refused in the server's words; any other code opens the next order
  const nextScan = async (code: string, action: FloorAction) => {
    const target = completed && view.relabel ? relabelFor(code, urgent?.actions ?? []) : null;
    if (!target || target.orderId !== orderId) return openNext(code);
    try {
      await api<UrgentAction>('POST', `/api/wms/floor-actions/${target.id}/done`, { code }, { action });
      feedback('done');
      setOutcome({ tone: 'ok', text: `${target.orderNumber}: yeni etiket okutuldu, iş kapandı. Koliyi Sevkiyat’a götürün.` });
      setVerification((v) => (v ? { ...v, relabel: undefined } : v));
      void loaded.reload();
      void urgent?.reload();
    } catch (e) {
      feedback('error');
      setOutcome({ tone: 'bad', text: errorMessage(e) });
    }
  };

  return (
    <>
      <div className="row">
        <a className="btn ghost" href="#/paketleme">
          <Icon name="back" /> Kuyruk
        </a>
        <h2 className="mono">{view.orderNumber}</h2>
        <SellerChip code={view.tenantCode} />
        <ChannelBadge channel={view.channel} />
        {view.pickTote && <span className="chip nowrap">{toteText(view.pickTote)}</span>}
        {view.parcels > 1 && <span className="chip info nowrap">{view.parcels} koli</span>}
        <span style={{ flex: 1 }} />
        <b>
          {packed} / {required} adet
        </b>
      </div>

      {/* KARGO DEĞİŞTİ (acil iş, 0069): the label printed before the carrier change is void; packed here, the
          new one prints at this desk. A parcel packed before the change waits for its new label's scan */}
      {view.relabel && (
        <div className="dns-alert relabel-alert" role="alert">
          <Icon name="alert" size={36} />
          <div>
            <b>{relabelBanner(view.relabel.details).headline}</b>
            <span>{completed ? view.relabel.instruction : relabelBanner(view.relabel.details).before}</span>
            {/* the scan field below takes the new label: the acil iş closes */}
            {completed && <span>Yeni etiketi koliye yapıştırınca aşağıdaki alana okutun (ya da Sevkiyat’ta okutun).</span>}
            {blocked && (
              <button className="btn" onClick={() => setRelabelSeen(true)}>
                <Icon name="check" /> Eski etiketi söktüm, devam et
              </button>
            )}
          </div>
        </div>
      )}

      {/* packed, then a line of it cancelled: the parcel was opened again (Sevkiyat refuses it until packed again) */}
      {view.repack && !completed && (
        <div className="banner bad" role="alert">
          <Icon name="alert" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="big">Yeniden paketlenecek: {cancelledLinesText(view.cancelledLines ?? [])} iptal edildi</div>
            Paketi açın, iptal edilen ürünü çıkarıp Yerleştirme → Rafa iade ile rafına koyun. Kalanı okutulmuş sayılır: “Paketlemeyi tamamla” ile yeniden
            paketleyin, etiket yeniden basılır.
          </div>
        </div>
      )}
      {!view.repack && !completed && view.cancelledLines && view.cancelledLines.length > 0 && (
        <div className="banner warn" role="status">
          <Icon name="alert" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="big">Pazaryeri iptal etti: {cancelledLinesText(view.cancelledLines)}</div>
            Koliye koymayın. Toplandıysa ürün Yerleştirme → Rafa iade görevinde; rafına geri koyun.
          </div>
        </div>
      )}
      {view.syncWarning && (
        <div className="banner warn" role="status">
          <Icon name="alert" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="big">{channelSyncWarningText(view.syncWarning)}</div>
            {CHANNEL_SYNC_ADVICE}
          </div>
        </div>
      )}

      {outcome && (
        <div className={`banner ${outcome.tone}`} role="alert">
          <div style={{ flex: 1 }}>
            <div className="big">{outcome.text}</div>
            {outcome.waiting === 'label' && <div style={{ fontWeight: 500 }}>Etiket birkaç dakikada hazırlanır; “Tekrar dene”ye basın. Ürünleri koliye koyabilirsiniz.</div>}
            {outcome.waiting === 'document' && (
              <div style={{ fontWeight: 500 }}>
                Satıcının belge ayarında bu belge zorunlu. Satıcı belgeyi yükleyince ya da e-Belgeler → Belge ayarlarında zorunluluğu kaldırınca “Tekrar dene”ye basın.
              </div>
            )}
          </div>
          {outcome.waiting && (
            <button className="btn" disabled={deskBusy} onClick={() => void (asIs ? desk.run('Etiketi bas, tamamla', packAsIs) : desk.run('Paketlemeyi tamamla', retry))}>
              <Icon name="refresh" /> Tekrar dene
            </button>
          )}
        </div>
      )}
      {/* many units on one label: fine when they share a box; a box without a label is not */}
      {completed && check && !labelsChecked && (
        <div className={`banner parcel-check ${check.kind === 'short' ? 'bad' : 'warn'}`} role="alert">
          <Icon name="alert" />
          <div className="stack" style={{ flex: 1, minWidth: 0, gap: 8 }}>
            <div className="big">
              {check.units} adet, {view.parcels} kargo etiketi
            </div>
            <span>
              {check.kind === 'short'
                ? `${neededText(check.needed, check.ownBoxes)}, ${check.needed} etiket gerekir. Kolileri sayın; etiketsiz koli kalmasın.`
                : view.parcelsFixed
                  ? 'Hepsi tek koliye girdiyse sorun yok.'
                  : 'Hepsi tek koliye girdiyse sorun yok. Ayrı kolilere girdiyse koli sayısını seçin; her koliye bir etiket basılır.'}
            </span>
            {view.parcelsFixed ? (
              <span>Bu etiketi pazaryeri ya da kargo firması verdi: koli sayısı buradan değişmez. Ek koli etiketi için vardiya sorumlusuna haber verin.</span>
            ) : (
              <ParcelCount parcels={view.parcels} packed busy={deskWaits} onSave={changeParcels} />
            )}
            <div className="row">
              <button className="btn" onClick={() => setLabelsChecked(true)}>
                <Icon name="check" size={16} /> {check.kind === 'short' ? 'Kolileri saydım, doğru' : 'Tek koli, doğru'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* the shortage dialog shows it while open */}
      {!shortLine && desk.banner}
      {held && (
        <div className="banner warn" role="status">
          <Icon name="alert" />
          <div className="held-body">
            <div className="big">Koliyi BEKLEYEN rafına koyun; eksik ürün toplanınca sipariş masaya döner.</div>
            <div className="held-reason">{view.holdReason}</div>
            {/* the reader goes on with the next tote's order */}
            <ScanField placeholder="Sıradaki siparişi okutun" allowTyping onScan={openNext} suggest={PACKING_ORDERS} />
            <button className="btn primary" onClick={() => navigate('/paketleme')}>
              Sıradaki sipariş
            </button>
          </div>
        </div>
      )}
      {finishOffered && asIs && (
        <div className="banner info">
          <Icon name="box" />
          <div style={{ flex: 1 }}>
            <div className="big">Paketlemeden gider</div>
            {view.labelTakenAlong
              ? 'Ürünler toplamada okutuldu ve etiketleri rafta yapıştırıldı; ürünler Sevkiyat alanında. Tamamlayın, etiket yeniden basılmaz.'
              : 'Ürünler toplamada okutuldu. Etiketi basın, her ürüne bir etiket yapıştırıp sevkiyata bırakın.'}
          </div>
          <button className="btn primary" disabled={deskBusy} onClick={() => void desk.run('Etiketi bas, tamamla', packAsIs)}>
            {view.labelTakenAlong ? (
              <>
                <Icon name="check" /> Tamamla
              </>
            ) : (
              <>
                <Icon name="printer" /> Etiketi bas, tamamla
              </>
            )}
          </button>
        </div>
      )}
      {finishOffered && !asIs && allScanned && (
        <div className="banner warn">
          <div style={{ flex: 1 }}>
            <div className="big">Tüm ürünler okutuldu</div>
            Paketleme henüz tamamlanmadı.
          </div>
          <button className="btn" disabled={deskBusy} onClick={() => void desk.run('Paketlemeyi tamamla', retry)}>
            <Icon name="check" /> Paketlemeyi tamamla
          </button>
        </div>
      )}

      {/* on a phone: the scan field, then these lines, then koli / adet (styles: .pack-lines) */}
      <section className={`card pack-lines ${flash}`} onAnimationEnd={() => setFlash('')}>
        {view.lines.map((line) => {
          const scanning = !completed && !held && line.packed < line.required;
          // units at the desk not scanned yet: the ones "Eksik / hasarlı" can report
          const unscanned = completed ? 0 : line.required - line.packed - line.missing;
          return (
            <div key={line.orderItemId} className={`pack-line${line.packed >= line.required ? ' done' : ''}${hit === line.orderItemId ? ' hit' : ''}`}>
              <ProductThumb name={line.name} imageUrl={line.imageUrl} small />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{line.name}</div>
                <div className="mono muted" style={{ fontSize: 13 }}>
                  {line.sku}
                </div>
                {line.missing > 0 && !completed && <span className="chip warn">{line.missing} adet eksik</span>}
              </div>
              <div className="count">
                {line.packed}/{line.required}
              </div>
              {/* a second row: buttons a thumb can hit */}
              <div className="pack-line-actions">
                {!line.unitLabels && scanning && (
                  <button
                    className="btn sm nowrap"
                    disabled={deskBusy}
                    onClick={() => void desk.run('Gözle onayla', (action) => scan(line.code, true, quantity, action))}
                    title="Bu ürünün üstünde etiket yok: görsele ve ada bakarak onaylayın"
                  >
                    <Icon name="eye" size={16} /> Etiketsiz · gözle onayla
                  </button>
                )}
                {line.unitLabels && isSupervisor && scanning && (
                  <button
                    className="btn sm nowrap"
                    disabled={deskBusy}
                    onClick={() => void desk.run('Okutmadan say', (action) => scan(line.code, true, line.required - line.packed, action))}
                    title="Barkod okutmadan, ürüne bakarak kalan adedi sayar"
                  >
                    <Icon name="check" size={16} /> Okutmadan say ({line.required - line.packed})
                  </button>
                )}
                {/* a unit whose barcode is missing or unreadable gets a label on the spot */}
                {!held && labelCode({ barcode: line.code, sku: line.sku }) && (
                  <button className="btn sm ghost nowrap" onClick={() => setProductLabel(line)} title="Bu ürünün barkod etiketini bu bilgisayarın yazıcısından basar">
                    <Icon name="tag" size={16} /> Ürün etiketi
                  </button>
                )}
                {unscanned > 0 && (
                  <button className="btn sm nowrap danger-text" disabled={deskWaits} onClick={() => setShortLine(line)} title="Sepette yok, kırık ya da yanlış ürün geldi">
                    <Icon name="alert" size={16} /> Eksik / hasarlı
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {completed ? (
        // the next tote's order while the table has more, else the floor's next work
        <NextWorkStep
          from="/paketleme"
          more={{ label: 'Sıradaki sipariş', to: '/paketleme', icon: 'box' }}
          tone="ok"
          title="Sipariş paketlendi"
          steps={[
            view.parcels > 1 ? `Kargo etiketlerini kolilere yapıştırın: ${view.parcels} koli, her koliye bir etiket.` : 'Kargo etiketini koliye yapıştırın.',
            `${view.parcels > 1 ? 'Kolileri' : 'Koliyi'} Sevkiyat rafında “${pileOf(view)}” yığınına koyun.`,
          ]}
        >
          <ScanField
            placeholder={view.relabel ? 'Yeni kargo etiketini okutun' : 'Sıradaki siparişi okutun'}
            allowTyping
            onScan={nextScan}
            suggest={PACKING_ORDERS}
          />
          {/* also for a parcel opened later: the label can always be printed again */}
          <div className="row">
            {/* a phone has no label printer: the label goes to the desk's printer from there */}
            <LabelState orderId={orderId} again={phone()} />
            {!phone() && (
              <button className="btn sm" onClick={() => void reprint()}>
                <Icon name="printer" size={16} /> Etiketi yeniden bas
              </button>
            )}
            <button className="btn sm ghost" onClick={() => setReprintWithDesi(true)} title="Etikete desi yazıp yeniden basar">
              Desi ekleyip bas
            </button>
            {!view.parcelsFixed && (
              <button className="btn sm ghost" onClick={() => setParcelsOpen(!parcelsOpen)} title="Sipariş birden çok koliye girdiyse her koliye bir etiket basar">
                Koli sayısı: {view.parcels}
              </button>
            )}
            <button
              className="btn sm ghost"
              disabled={deskWaits}
              onClick={() => void desk.run('Paketlemeyi geri al', unpack)}
              title="Paket henüz kargoya verilmediyse masaya geri alır; stok geri yazılır"
            >
              <Icon name="undo" size={16} /> Paketlemeyi geri al
            </button>
          </div>
          {parcelsOpen && <ParcelCount parcels={view.parcels} packed busy={deskWaits} onSave={changeParcels} />}
        </NextWorkStep>
      ) : held ? null : (
        <>
          <section className="card pack-opts">
            <div className="card-b stack">
              {/* several units: one box, or one label per box (own-box goods are a box each) */}
              {(required > 1 || view.parcels > 1) && (
                <div className="field">
                  <label>Koli sayısı · her koliye bir kargo etiketi</label>
                  {view.parcelsFixed ? (
                    <span className="muted">
                      {view.parcels} koli. Bu etiketi pazaryeri ya da kargo firması verdi: koli sayısı buradan değişmez.
                    </span>
                  ) : (
                    <ParcelCount parcels={view.parcels} packed={false} busy={deskWaits} onSave={changeParcels} />
                  )}
                  {ahead && ahead.needed > 1 && (
                    <span className={view.parcels < ahead.needed ? 'chip bad' : 'muted'}>{neededText(ahead.needed, ahead.ownBoxes)}.</span>
                  )}
                </div>
              )}
              <div className="field">
                <label>Koli tipi</label>
                <div className="row">
                  <button className={`chip select${carton === null ? ' on' : ''}`} onClick={() => setCarton(null)}>
                    Otomatik desi
                  </button>
                  {cartons
                    .filter((c) => c.isActive)
                    .map((c) => (
                      <button key={c.code} className={`chip select${carton === c.code ? ' on' : ''}`} onClick={() => setCarton(c.code)} title={c.name}>
                        {c.code} · {fmt.dec(c.desi)} desi
                      </button>
                    ))}
                </div>
              </div>
              <div className="row">
                {props.desi ? (
                  <>
                    <span className="chip info">Etikete desi: {props.desi}</span>
                    <button className="btn sm ghost" onClick={() => setDesiOpen(true)}>
                      Değiştir
                    </button>
                  </>
                ) : (
                  <button className="btn sm" onClick={() => setDesiOpen(true)} title="Kargo etiketimize desi yazılmaz; isterseniz burada ekleyin">
                    Desi ekle
                  </button>
                )}
              </div>
              <div className="field">
                <label>Bir okutmada sayılacak adet</label>
                <div className="row">
                  {[1, 2, 3, 5, 10].map((n) => (
                    <button key={n} className={`chip select${quantity === n ? ' on' : ''}`} onClick={() => setQuantity(n)}>
                      ×{n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section className="card pack-scan">
            <div className="card-b stack">
              {/* back from the exceptions queue: right above the field a phone scrolls to */}
              {view.shelved && (
                <div className="banner info">
                  <Icon name="box" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="big">Koli BEKLEYEN rafında</div>
                    Koliyi masaya alın; önceki okutmalar duruyor. Kalan ürünleri okutun.
                  </div>
                </div>
              )}
              <ScanField
                placeholder={blocked ? 'Önce üstteki KARGO DEĞİŞTİ uyarısını okuyun' : 'Ürün barkodunu okutun'}
                onScan={(code, action) => scan(code, false, quantity, action)}
                onHeld={setScanHeld}
                disabled={blocked}
                hint="Her okutma siparişle karşılaştırılır; yanlış ürün ve fazla adet reddedilir."
              />
              {/* an own-box unit was read at its bin, never at this desk: no scan to take back */}
              {packed > 0 && (
                <div className="row">
                  <button
                    className="btn"
                    disabled={deskWaits || scanHeld}
                    onClick={() => void desk.run('Son okutmayı geri al', undoScan)}
                    title={scanHeld ? 'Önce bağlantısı kopan okutmanın sonucu belli olmalı' : 'Masadaki sayaçtan son okutmayı düşer'}
                  >
                    <Icon name="undo" /> Son okutmayı geri al
                  </button>
                </div>
              )}
            </div>
          </section>
        </>
      )}
      {desiOpen && (
        <DesiDialog
          desi={props.desi}
          onClose={() => setDesiOpen(false)}
          onSave={(desi) => {
            props.onDesi(desi);
            setDesiOpen(false);
          }}
        />
      )}
      {shortLine && (
        <ShortageDialog line={shortLine} runner={desk} onClose={() => setShortLine(null)} onReport={(reason, qty, action) => reportShortage(shortLine, reason, qty, action)} />
      )}
      {reprintWithDesi && <DocumentPreview orderId={orderId} orderNumber={view.orderNumber} kind="shipping_label" desi onClose={() => setReprintWithDesi(false)} />}
      {productLabel && (
        <ProductLabelDialog
          products={[
            {
              id: productLabel.productId,
              sku: productLabel.sku,
              name: productLabel.name,
              barcode: productLabel.code === productLabel.sku ? null : productLabel.code,
              sellerCode: view.tenantCode,
              trackLot: false,
              trackExpiry: false,
              unitLabels: productLabel.unitLabels,
              physical: 0,
            },
          ]}
          onClose={() => {
            setProductLabel(null);
            // the label just printed is scanned next: the field takes the reader back
            setTimeout(() => document.querySelector<HTMLInputElement>('.scan input')?.focus(), 0);
          }}
        />
      )}
    </>
  );
}

/**
 * Koli sayısı: − n koli +. Before packing nothing prints, so each tap is saved; `packed`: the
 * order's labels print again at the desk, so the count is chosen first and sent once.
 */
function ParcelCount({ parcels, packed, busy, onSave }: { parcels: number; packed: boolean; busy: boolean; onSave: (count: number) => void }) {
  const [draft, setDraft] = useState(parcels);
  useEffect(() => setDraft(parcels), [parcels]);
  const step = (next: number) => (packed ? setDraft(next) : onSave(next));
  return (
    <div className="row parcel-count">
      <button className="btn" disabled={busy || draft <= 1} onClick={() => step(draft - 1)} aria-label="Bir koli az">
        −
      </button>
      <b className="parcel-n" aria-live="polite">
        {draft} koli
      </b>
      <button className="btn" disabled={busy || draft >= MAX_PARCELS} onClick={() => step(draft + 1)} aria-label="Bir koli fazla">
        <Icon name="plus" size={16} />
      </button>
      {packed && draft !== parcels && (
        <button className="btn primary" disabled={busy} onClick={() => onSave(draft)}>
          {packed ? (
            <>
              <Icon name="printer" size={16} /> {draft} etiket bas
            </>
          ) : (
            'Kaydet'
          )}
        </button>
      )}
    </div>
  );
}

/** The desi for the order's label: in a window, so a scan meant for the desk never lands in it. */
function DesiDialog(props: { desi: string | null; onClose: () => void; onSave: (desi: string | null) => void }) {
  const [raw, setRaw] = useState(props.desi ?? '');
  const desi = desiText(raw);
  return (
    <Modal
      title="Etikete desi ekle"
      onClose={props.onClose}
      footer={
        <>
          {props.desi && (
            <button className="btn ghost" style={{ marginRight: 'auto' }} onClick={() => props.onSave(null)}>
              Desiyi kaldır
            </button>
          )}
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={!desi} onClick={() => props.onSave(desi)}>
            Kaydet
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field" style={{ maxWidth: 160 }}>
          <label htmlFor="desk-desi">Desi</label>
          <input
            id="desk-desi"
            className={`input${raw && !desi ? ' invalid' : ''}`}
            inputMode="decimal"
            autoFocus
            placeholder="ör. 3"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && desi) props.onSave(desi);
            }}
          />
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Sipariş paketlenince bu masada basılan kargo etiketine yazılır. Pazaryerinin kendi etiketine eklenmez.
        </p>
      </div>
    </Modal>
  );
}

/** On the desk's banner, also after the dialog closed: "“Eksik / hasarlı” bildirimi YAPILDI" */
const SHORTAGE_WORDS: ActionWords = { what: '“Eksik / hasarlı” bildirimi', done: 'YAPILDI', notDone: 'YAPILMADI' };

const SHORTAGE_HINTS: Readonly<Record<PackingShortageReason, string>> = {
  missing: 'Ürün sepette değil. Gözü sayılır.',
  damaged: 'Kırık, ezik ya da bozuk. Hasarlı alana gider.',
  wrong_product: 'Sepette başka ürün var. Gözü sayılır.',
};

/**
 * "Eksik / hasarlı": why units of a line cannot go into the parcel, and how many. The report runs
 * on the desk's runner: a lost one keeps its key after the dialog closes, is sent again from the
 * desk, and holds the desk's buttons (another report with a new key would book the units twice).
 */
function ShortageDialog(props: {
  line: PackingLine;
  runner: FloorActionRunner;
  onClose: () => void;
  onReport: (reason: PackingShortageReason, qty: number, action: FloorAction) => Promise<void>;
}) {
  const { line, runner } = props;
  const most = line.required - line.packed - line.missing;
  const [reason, setReason] = useState<PackingShortageReason | null>(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (action: FloorAction) => {
    if (!reason) return;
    setBusy(true);
    try {
      await props.onReport(reason, qty, action);
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  // not while the report is on its way: its answer decides what the desk shows
  const close = () => {
    if (!busy) props.onClose();
  };
  return (
    <Modal
      title="Eksik / hasarlı"
      onClose={close}
      footer={
        <>
          <button className="btn big" disabled={busy} onClick={close}>
            Vazgeç
          </button>
          <button
            className="btn primary big"
            disabled={!reason || busy || runner.waiting}
            onClick={() =>
              void runner.run(SHORTAGE_WORDS, submit).then((sent) => {
                if (!sent) setError('Önceki işlem henüz bitmedi. Bekleyip tekrar deneyin.');
              })
            }
          >
            Bildir
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        {runner.banner}
        <div style={{ minWidth: 0 }}>
          <b>{line.name}</b> <span className="mono muted">{line.sku}</span>
        </div>
        <div className="shortage-choices" role="radiogroup" aria-label="Neden">
          {PACKING_SHORTAGE_REASONS.map((r) => (
            <button key={r} className={`btn shortage-choice${reason === r ? ' on' : ''}`} role="radio" aria-checked={reason === r} onClick={() => setReason(r)}>
              {PACKING_SHORTAGE_LABELS[r]}
              <small>{SHORTAGE_HINTS[r]}</small>
            </button>
          ))}
        </div>
        {most > 1 && (
          <div className="field">
            <label>Kaç adet?</label>
            <div className="shortage-qty">
              <button className="btn" aria-label="Bir azalt" disabled={qty <= 1} onClick={() => setQty((n) => Math.max(1, n - 1))}>
                −
              </button>
              <span className="n">{qty}</span>
              <button className="btn" aria-label="Bir artır" disabled={qty >= most} onClick={() => setQty((n) => Math.min(most, n + 1))}>
                <Icon name="plus" size={18} />
              </button>
              <span className="muted">en fazla {most}</span>
            </div>
          </div>
        )}
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Sipariş sorunlu siparişlere düşer, okutulanlar silinmez. Koliyi BEKLEYEN rafına koyun.
        </p>
      </div>
    </Modal>
  );
}
